import { useEffect, useMemo, useState } from 'react'
import { Download, ChevronDown, ChevronRight, X } from 'lucide-react'
import { api } from '@/api/client'
import type { AuditEntry, AuditFilters, AuditResponse } from './types'

type RangePreset = '24h' | '7d' | '30d' | 'all' | 'custom'

function isoNow(): string { return new Date().toISOString() }
function isoMinusHours(h: number): string { return new Date(Date.now() - h * 3600_000).toISOString() }
function isoMinusDays(d: number): string { return isoMinusHours(d * 24) }

function presetRange(p: RangePreset): { from?: string; to?: string } {
  switch (p) {
    case '24h':    return { from: isoMinusHours(24), to: isoNow() }
    case '7d':     return { from: isoMinusDays(7),   to: isoNow() }
    case '30d':    return { from: isoMinusDays(30),  to: isoNow() }
    case 'all':    return {}
    case 'custom': return {}
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function prettyJson(raw: string | null): string {
  if (!raw) return ''
  try { return JSON.stringify(JSON.parse(raw), null, 2) } catch { return raw }
}

function AuditRow({ e }: { e: AuditEntry }) {
  const [open, setOpen] = useState(false)
  const hasDetails = !!e.Details && e.Details !== '{}' && e.Details !== 'null'
  return (
    <>
      <tr className={`border-b border-gray-100 hover:bg-gray-50 ${hasDetails ? 'cursor-pointer' : ''}`}
          onClick={() => hasDetails && setOpen(!open)}>
        <td className="px-3 py-2 w-8">
          {hasDetails && (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
        </td>
        <td className="px-3 py-2 text-gray-700 whitespace-nowrap" title={e.CreatedAt}>{formatDateTime(e.CreatedAt)}</td>
        <td className="px-3 py-2 text-gray-700">{e.FirmaId ?? '—'}</td>
        <td className="px-3 py-2 text-gray-700">{e.UserName ?? <span className="text-gray-400 italic">System</span>}</td>
        <td className="px-3 py-2"><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{e.Action}</code></td>
        <td className="px-3 py-2 text-gray-700 text-xs">
          {e.TargetType ? `${e.TargetType}:${e.TargetId ?? ''}` : '—'}
        </td>
        <td className="px-3 py-2 text-gray-500 text-xs">{e.IpAddress ?? '—'}</td>
      </tr>
      {open && hasDetails && (
        <tr>
          <td colSpan={7} className="bg-gray-50 px-6 py-3">
            <pre className="text-xs bg-white border border-gray-200 rounded p-3 overflow-x-auto">{prettyJson(e.Details)}</pre>
          </td>
        </tr>
      )}
    </>
  )
}

export default function AuditTab() {
  const [actions, setActions] = useState<string[]>([])
  const [preset, setPreset] = useState<RangePreset>('7d')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [filters, setFilters] = useState<AuditFilters>({ page: 1 })
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [data, setData] = useState<AuditResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pageSize = 50

  // Actions für Dropdown
  useEffect(() => {
    api.superadmin.auditActions().then((r) => setActions(r.actions)).catch(() => {})
  }, [])

  // Q debounce
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 300)
    return () => clearTimeout(id)
  }, [q])

  // Range → filters
  useEffect(() => {
    if (preset === 'custom') {
      setFilters((f) => ({ ...f, from: customFrom || undefined, to: customTo || undefined, page: 1 }))
    } else {
      const r = presetRange(preset)
      setFilters((f) => ({ ...f, from: r.from, to: r.to, page: 1 }))
    }
  }, [preset, customFrom, customTo])

  // Q → filters
  useEffect(() => {
    setFilters((f) => ({ ...f, q: debouncedQ || undefined, page: 1 }))
  }, [debouncedQ])

  // Load audit
  useEffect(() => {
    setData(null)
    api.superadmin.audit(filters)
      .then(setData)
      .catch((e) => setError(e.message))
  }, [filters])

  const totalPages = useMemo(() => data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1, [data])
  const csvUrl = useMemo(() => api.superadmin.auditCsvUrl(filters), [filters])

  const resetFilters = () => {
    setPreset('7d')
    setCustomFrom('')
    setCustomTo('')
    setQ('')
    setFilters({ page: 1 })
  }

  return (
    <div className="space-y-4">
      {/* Filter-Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Firma-ID</label>
            <input
              type="number"
              value={filters.firmaId ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, firmaId: e.target.value ? Number(e.target.value) : undefined, page: 1 }))}
              placeholder="optional"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm w-32"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Action</label>
            <select
              value={filters.action ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value || undefined, page: 1 }))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">Alle</option>
              {actions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Zeitraum</label>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value as RangePreset)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="24h">24 Stunden</option>
              <option value="7d">7 Tage</option>
              <option value="30d">30 Tage</option>
              <option value="all">Alle</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {preset === 'custom' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Von</label>
                <input type="datetime-local" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                       className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Bis</label>
                <input type="datetime-local" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                       className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </>
          )}

          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Volltext (details)</label>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="z.B. Account-ID oder IP"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>

          <button
            onClick={resetFilters}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            title="Filter zurücksetzen"
          >
            <X size={14} /> Zurücksetzen
          </button>

          <a
            href={csvUrl}
            className="px-4 py-2 bg-fom-500 hover:bg-fom-600 text-white text-sm rounded-lg flex items-center gap-2 no-underline"
          >
            <Download size={14} /> CSV
          </a>
        </div>
      </div>

      {error && <div className="p-4 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}

      {/* Tabelle */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left"></th>
              <th className="px-3 py-2 text-left">Zeit</th>
              <th className="px-3 py-2 text-left">Firma</th>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Action</th>
              <th className="px-3 py-2 text-left">Target</th>
              <th className="px-3 py-2 text-left">IP</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={7} className="px-3 py-4 text-sm text-gray-500">Lade…</td></tr>}
            {data && data.entries.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-sm text-gray-500">Keine Einträge.</td></tr>}
            {data?.entries.map((e) => <AuditRow key={e.Id} e={e} />)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Seite {data.page} von {totalPages} · {data.total} Einträge
          </p>
          <div className="flex gap-2">
            <button
              disabled={data.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
              className="px-3 py-1.5 border border-gray-200 rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Zurück
            </button>
            <button
              disabled={data.page >= totalPages}
              onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
              className="px-3 py-1.5 border border-gray-200 rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Weiter
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
